import { useMemo } from 'react'

import { computeVatNumber } from '@features/companies/utils/computeVatNumber'
import { getDefaultIbanPaymentFields } from '@features/invoices/utils/getDefaultIbanPaymentFields'
import { useZervantSettings } from '@features/invoicingSettings/hooks/useZervantSettings'
import { getDefaultLegalStatusValues } from '@features/invoicingSettings/utils/getDefaultLegalStatusValues'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import type { PredefinedCompanyValues } from '../../types/missingComplianceDetails/predefinedCompanyValues'

export const usePredefinedCompanyValues = () => {
  const { organization, isLoading: isLoadingUserOrganization } = useUserOrganization()
  const { data: zervantSettings, isLoading: isLoadingZervantSettings } = useZervantSettings()

  const isLoading = isLoadingZervantSettings || isLoadingUserOrganization

  const defaultPaymentFields = useMemo(
    () => getDefaultIbanPaymentFields(zervantSettings?.values?.paymentMethods),
    [zervantSettings],
  )

  const defaultLegalStatusFields = useMemo(() => getDefaultLegalStatusValues(zervantSettings), [zervantSettings])

  const { addressLine2, city, countryId, isVatExempted, name, registrationNo, street, vatNo, zipcode } =
    organization || {}
  const { bank, iban, swift } = defaultPaymentFields || {}

  const computedVatNo = (!vatNo && !isVatExempted && computeVatNumber(countryId, registrationNo)) || undefined

  const predefinedCompanyValues: PredefinedCompanyValues = useMemo(
    () => ({
      addressLine2: addressLine2 || '',
      ...(computedVatNo && { autoCompletedFields: { vatNo: true } }),
      bank,
      companyAddress: street,
      companyCity: city,
      companyName: name,
      companyZipCode: zipcode,
      countryId,
      iban,
      isSiretPendingAssignment: zervantSettings?.values?.company?.legalFormSettings?.isSiretPendingAssignment ?? false,
      isVatExempted: isVatExempted ?? false,
      legalStatusCustom: defaultLegalStatusFields.legalStatusCustom,
      legalStatus: defaultLegalStatusFields.legalStatusOption,
      registrationNo,
      sharedCapital: defaultLegalStatusFields.sharedCapital,
      swift,
      vatNo: vatNo || computedVatNo || '',
    }),
    [
      addressLine2,
      bank,
      city,
      computedVatNo,
      countryId,
      defaultLegalStatusFields,
      iban,
      isVatExempted,
      name,
      registrationNo,
      street,
      swift,
      vatNo,
      zervantSettings?.values?.company?.legalFormSettings?.isSiretPendingAssignment,
      zipcode,
    ],
  )

  return { isLoading, predefinedCompanyValues, defaultLegalStatusFields }
}
